import {FC, useEffect, useMemo, useState} from 'react'
import {
  CheckCircle2,
  KeyRound,
  LayoutGrid,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'

import * as API from '../../api'
import {useAccessMatrixFilters} from '../../components/AccessMatrixFilters'
import PermissionsModal from './PermissionsModal'
import UserBulkPermissionsModal from './UserBulkPermissionsModal'

type Column = {
  id: number
  kind: 'product' | 'subproduct'
  label: string
  parent?: string
  item: any
}

function normalizePermissionCategories(data: any[]) {
  return data.map((category) => {
    const products = category.products || []
    const subproducts = products.flatMap((product: any) => product.subproducts || [])

    return {
      ...category,
      products,
      users: (category.users || []).map((user: any) => {
        const productState = new Map(
          (user.products || []).map((permission: any) => [
            permission.id_product,
            Boolean(permission.enabled),
          ])
        )
        const subproductState = new Map(
          (user.subproducts || []).map((permission: any) => [
            permission.id_subproduct,
            Boolean(permission.enabled),
          ])
        )

        return {
          ...user,
          products: products.map((product: any) => ({
            id_product: product.id_product,
            enabled: productState.get(product.id_product) === true,
          })),
          subproducts: subproducts.map((subproduct: any) => ({
            id_subproduct: subproduct.id_subproduct,
            enabled: subproductState.get(subproduct.id_subproduct) === true,
          })),
        }
      }),
    }
  })
}

const UsersPermissionMatrix: FC = () => {
  const {
    activeCategory,
    setActiveCategory,
    selectedEntities: selectedUsers,
    setSelectedEntities: setSelectedUsers,
    permissionQuery,
    setPermissionQuery,
  } = useAccessMatrixFilters()
  const [categories, setCategories] = useState<any[]>([])
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  const [feedback, setFeedback] = useState('')
  const [selectedMatrixUser, setSelectedMatrixUser] = useState<any>(null)
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false)
  const [userBulkSaving, setUserBulkSaving] = useState(false)

  async function update(showLoading = true) {
    if (showLoading) setLoading(true)
    setLoadError(false)

    try {
      const response = await API.loadUsersPermissions()
      const data = normalizePermissionCategories(response.data || [])
      setCategories(data)
      setActiveCategory((current) => {
        if (data.some((category: any) => category.id_category === current)) return current
        return data[0]?.id_category ?? null
      })
    } catch {
      setLoadError(true)
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    update()
  }, [])

  const category = useMemo(
    () => categories.find((item) => item.id_category === activeCategory),
    [activeCategory, categories]
  )

  const userOptions = useMemo(
    () =>
      (category?.users || []).map((user: any) => ({
        value: user.id_user,
        label: user.name,
        ...user,
      })),
    [category]
  )

  const selectedUserIds = new Set(selectedUsers.map((user) => `${user.value}`))
  const displayedUsers = selectedUsers.length
    ? (category?.users || []).filter((user: any) => selectedUserIds.has(`${user.id_user}`))
    : category?.users || []

  const allColumns = useMemo<Column[]>(
    () =>
      (category?.products || []).flatMap((product: any) => [
        {
          id: product.id_product,
          kind: 'product' as const,
          label: product.admin_name || product.name,
          item: product,
        },
        ...(product.subproducts || []).map((subproduct: any) => ({
          id: subproduct.id_subproduct,
          kind: 'subproduct' as const,
          label: subproduct.admin_name || subproduct.name,
          parent: product.admin_name || product.name,
          item: subproduct,
        })),
      ]),
    [category]
  )

  const columns = useMemo<Column[]>(() => {
    const normalizedQuery = permissionQuery.trim().toLocaleLowerCase('pt-BR')

    if (!normalizedQuery) return allColumns
    return allColumns.filter((column: Column) =>
      `${column.label} ${column.parent || ''}`.toLocaleLowerCase('pt-BR').includes(normalizedQuery)
    )
  }, [allColumns, permissionQuery])

  const enabledCount = useMemo(
    () =>
      (category?.users || []).reduce((total: number, user: any) => {
        const products = (user.products || []).filter((item: any) => item.enabled).length
        const subproducts = (user.subproducts || []).filter((item: any) => item.enabled).length
        return total + products + subproducts
      }, 0),
    [category]
  )

  function isEnabled(user: any, column: Column) {
    if (column.kind === 'product') {
      return Boolean(user.products?.find((item: any) => item.id_product === column.id)?.enabled)
    }
    return Boolean(user.subproducts?.find((item: any) => item.id_subproduct === column.id)?.enabled)
  }

  function updateLocalPermission(userId: number, column: Column, enabled: boolean) {
    setCategories((current) =>
      current.map((categoryItem) => {
        const columnExists =
          column.kind === 'product'
            ? (categoryItem.products || []).some((product: any) => product.id_product === column.id)
            : (categoryItem.products || []).some((product: any) =>
                (product.subproducts || []).some(
                  (subproduct: any) => subproduct.id_subproduct === column.id
                )
              )
        if (!columnExists) return categoryItem

        return {
          ...categoryItem,
          users: categoryItem.users.map((user: any) => {
            if (user.id_user !== userId) return user
            const collection = column.kind === 'product' ? 'products' : 'subproducts'
            const idKey = column.kind === 'product' ? 'id_product' : 'id_subproduct'
            const exists = (user[collection] || []).some((item: any) => item[idKey] === column.id)

            return {
              ...user,
              [collection]: exists
                ? user[collection].map((item: any) =>
                    item[idKey] === column.id ? {...item, enabled} : item
                  )
                : [...(user[collection] || []), {[idKey]: column.id, enabled}],
            }
          }),
        }
      })
    )
  }

  async function changePermission(user: any, column: Column, checked: boolean) {
    const cellKey = `${user.id_user}-${column.kind}-${column.id}`
    setSavingCells((current) => new Set(current).add(cellKey))
    setFeedback('')
    updateLocalPermission(user.id_user, column, checked)

    try {
      let response: any
      if (column.kind === 'product') {
        const payload = {id_product: column.id, id_user: user.id_user}
        response = checked
          ? await API.addProductUserPermission(payload)
          : await API.removeProductUserPermission(payload)
      } else {
        const payload = {id_subproduct: column.id, id_user: user.id_user}
        response = checked
          ? await API.addSubProductUserPermission(payload)
          : await API.removeSubProductUserPermission(payload)
      }
      if (response?.success === false) throw new Error('Permission update failed')
      await update(false)
    } catch {
      updateLocalPermission(user.id_user, column, !checked)
      await update(false)
      setFeedback('Não foi possível salvar uma das alterações. Tente novamente.')
    } finally {
      setSavingCells((current) => {
        const next = new Set(current)
        next.delete(cellKey)
        return next
      })
    }
  }

  async function onPermissionModalRequest(selectAll: boolean) {
    if (!selectedItem) return

    if (selectedItem.id_subproduct) {
      if (selectAll) await API.selectAllUsersIntoSubproduct(selectedItem.id_subproduct)
      else await API.unselectAllUsersIntoSubproduct(selectedItem.id_subproduct)
    } else if (selectAll) {
      await API.selectAllUsersIntoProduct(selectedItem.id_product)
    } else {
      await API.unselectAllUsersIntoProduct(selectedItem.id_product)
    }

    await update(false)
    setShowPermissionsModal(false)
  }

  async function onUserPermissionRequest(selectAll: boolean) {
    if (!selectedMatrixUser) return
    const targets = allColumns.filter(
      (column) => isEnabled(selectedMatrixUser, column) !== selectAll
    )
    setUserBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        targets.map((column) => {
          if (column.kind === 'product') {
            const payload = {id_product: column.id, id_user: selectedMatrixUser.id_user}
            return selectAll
              ? API.addProductUserPermission(payload)
              : API.removeProductUserPermission(payload)
          }

          const payload = {id_subproduct: column.id, id_user: selectedMatrixUser.id_user}
          return selectAll
            ? API.addSubProductUserPermission(payload)
            : API.removeSubProductUserPermission(payload)
        })
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk permission update failed')
      }
      await update(false)
      setFeedback(
        `${
          selectAll ? 'Todas as permissões foram concedidas' : 'Todas as permissões foram removidas'
        } para ${selectedMatrixUser.name} em ${category?.admin_name || category?.name}.`
      )
      setShowUserPermissionsModal(false)
    } catch {
      await update(false)
      setFeedback('Não foi possível concluir a alteração para este usuário. Tente novamente.')
    } finally {
      setUserBulkSaving(false)
    }
  }

  const selectedMatrixUserActiveCount = selectedMatrixUser
    ? allColumns.filter((column) => isEnabled(selectedMatrixUser, column)).length
    : 0

  const hasFilters = Boolean(selectedUsers.length || permissionQuery)

  return (
    <section className='ua-section'>
      <Modal
        id='modal_permissions'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-450px ua-admin-modal-dialog'
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
      >
        {selectedItem && (
          <PermissionsModal
            item={selectedItem}
            onChange={onPermissionModalRequest}
            onClose={() => setShowPermissionsModal(false)}
          />
        )}
      </Modal>
      <Modal
        id='modal_user_permissions'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-500px ua-admin-modal-dialog'
        show={showUserPermissionsModal}
        onHide={() => !userBulkSaving && setShowUserPermissionsModal(false)}
      >
        {selectedMatrixUser && (
          <UserBulkPermissionsModal
            user={selectedMatrixUser}
            scopeLabel={category?.admin_name || category?.name || 'categoria atual'}
            itemLabel='permissões'
            totalCount={allColumns.length}
            activeCount={selectedMatrixUserActiveCount}
            loading={userBulkSaving}
            onChange={onUserPermissionRequest}
            onClose={() => setShowUserPermissionsModal(false)}
          />
        )}
      </Modal>

      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <KeyRound size={20} />
          </span>
          <div>
            <h2>Permissões</h2>
            <p>Controle quais produtos, áreas e menus cada pessoa pode acessar.</p>
          </div>
        </div>
        <span className='ua-badge ua-badge--success'>Alterações salvas automaticamente</span>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando permissões…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <KeyRound size={22} />
            </span>
            <h3>Não foi possível carregar as permissões</h3>
            <p>Tente novamente para acessar a matriz de produtos e menus.</p>
            <button type='button' className='ua-secondary-button mt-4' onClick={() => update()}>
              Tentar novamente
            </button>
          </div>
        </div>
      ) : categories.length ? (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <LayoutGrid size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{categories.length}</strong>
                <small className='ua-stat__label'>Categorias de acesso</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <UsersRound size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{category?.users?.length || 0}</strong>
                <small className='ua-stat__label'>Usuários nesta categoria</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <ShieldCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>
                  {(category?.products || []).reduce(
                    (total: number, product: any) => total + 1 + (product.subproducts?.length || 0),
                    0
                  )}
                </strong>
                <small className='ua-stat__label'>Permissões disponíveis</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <CheckCircle2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{enabledCount}</strong>
                <small className='ua-stat__label'>Acessos concedidos</small>
              </span>
            </div>
          </div>

          <div className='ua-card ua-category-picker'>
            <div className='ua-card__header'>
              <div>
                <h3>Categoria de acesso</h3>
                <p>Escolha uma área para visualizar e editar suas permissões.</p>
              </div>
            </div>
            <div className='ua-category-picker__list'>
              {categories.map((item) => {
                const isActive = item.id_category === activeCategory
                return (
                  <button
                    type='button'
                    key={item.id_category}
                    className={`ua-category-button ${isActive ? 'is-active' : ''}`}
                    onClick={() => setActiveCategory(item.id_category)}
                  >
                    <span>
                      <strong>{item.admin_name || item.name}</strong>
                      {item.admin_name && <small>{item.name}</small>}
                    </span>
                    <span className='ua-category-button__count'>{item.products?.length || 0}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className='ua-toolbar'>
            <div className='ua-field ua-field--wide'>
              <label htmlFor='permission-user-filter'>Usuários</label>
              <Select
                inputId='permission-user-filter'
                isMulti
                options={userOptions}
                value={selectedUsers}
                closeMenuOnSelect={false}
                placeholder='Todos os usuários'
                noOptionsMessage={() => 'Nenhum usuário encontrado'}
                onChange={(selected: any) => setSelectedUsers(selected || [])}
                classNamePrefix='admin-select'
              />
            </div>
            <div className='ua-field'>
              <label htmlFor='permission-name-filter'>Produto ou menu</label>
              <div className='ua-search'>
                <Search size={15} />
                <input
                  id='permission-name-filter'
                  value={permissionQuery}
                  placeholder='Buscar permissão'
                  onChange={(event) => setPermissionQuery(event.target.value)}
                />
                {permissionQuery && (
                  <button
                    type='button'
                    className='ua-search__clear'
                    aria-label='Limpar busca de permissão'
                    onClick={() => setPermissionQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            {hasFilters && (
              <button
                type='button'
                className='ua-secondary-button'
                onClick={() => {
                  setSelectedUsers([])
                  setPermissionQuery('')
                }}
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>

          {feedback && <div className='ua-feedback ua-feedback--danger'>{feedback}</div>}

          <div className='ua-card'>
            <div className='ua-card__header'>
              <div>
                <h3>{category?.admin_name || category?.name}</h3>
                <p>
                  {displayedUsers.length} usuário(s) · {columns.length} permissão(ões) visível(is)
                </p>
              </div>
              <span className='ua-matrix-tip'>
                Clique em uma coluna ou usuário para aplicar em massa
              </span>
            </div>

            {columns.length && displayedUsers.length ? (
              <div className='ua-table-wrap ua-matrix-wrap'>
                <table className='ua-table ua-matrix'>
                  <thead>
                    <tr>
                      <th className='ua-sticky-column'>Usuário</th>
                      {columns.map((column) => (
                        <th key={`${column.kind}-${column.id}`} className='ua-matrix__permission'>
                          <button
                            type='button'
                            className='ua-matrix-header-button'
                            title={`Aplicar ${column.label} a todos os usuários`}
                            onClick={() => {
                              setSelectedItem({...column.item, name: column.label})
                              setShowPermissionsModal(true)
                            }}
                          >
                            {column.parent && <small>{column.parent}</small>}
                            <span>{column.label}</span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map((user: any) => (
                      <tr key={user.id_user}>
                        <td className='ua-sticky-column'>
                          <button
                            type='button'
                            className='ua-user-trigger'
                            title={`Gerenciar todas as permissões de ${user.name}`}
                            onClick={() => {
                              setSelectedMatrixUser(user)
                              setShowUserPermissionsModal(true)
                            }}
                          >
                            <span className='ua-user-cell'>
                              <span className='ua-avatar'>
                                {(user.name || '?')
                                  .split(/\s+/)
                                  .slice(0, 2)
                                  .map((part: string) => part[0])
                                  .join('')
                                  .toUpperCase()}
                              </span>
                              <span className='ua-user-cell__copy'>
                                <strong>{user.name}</strong>
                                <small>{user.email || 'Usuário da empresa'}</small>
                              </span>
                            </span>
                            <span className='ua-user-trigger__action'>
                              <SlidersHorizontal size={14} />
                            </span>
                          </button>
                        </td>
                        {columns.map((column) => {
                          const cellKey = `${user.id_user}-${column.kind}-${column.id}`
                          return (
                            <td
                              key={cellKey}
                              className={`ua-matrix__cell ${
                                savingCells.has(cellKey) ? 'is-saving' : ''
                              }`}
                            >
                              <label className='ua-switch'>
                                <input
                                  type='checkbox'
                                  checked={isEnabled(user, column)}
                                  disabled={savingCells.has(cellKey)}
                                  aria-label={`${column.label} para ${user.name}`}
                                  onChange={(event) =>
                                    changePermission(user, column, event.target.checked)
                                  }
                                />
                                <span />
                              </label>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='ua-empty ua-empty--inside'>
                <div>
                  <span className='ua-empty__icon'>
                    <Search size={22} />
                  </span>
                  <h3>Nenhum resultado nesta matriz</h3>
                  <p>Altere os usuários selecionados ou a busca por permissão.</p>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <KeyRound size={22} />
            </span>
            <h3>Nenhuma permissão disponível</h3>
            <p>Não há categorias de acesso configuradas para esta empresa.</p>
          </div>
        </div>
      )}
    </section>
  )
}

export default UsersPermissionMatrix
